import Decimal from "decimal.js";
import { z } from "zod";
import { PlaidAccountTypeSchema } from "../Accounts/requests/getClientFundingAccounts";
import { ManualAccountType } from "../Accounts/requests/getManualAccountDetailVanadium";
import { AccountType, AdvisorId, AltAssetId, ClientId, Custodian, CustodianAccountId, ExternalAccountId, FartherAccountId, PaginationMetadata, PortfolioFamilyChild, } from "../Types";
export const HouseholdIdSchema = z
    .string()
    .uuid()
    .brand("HouseholdId")
    .describe("ID of the household");
export const HouseholdMemberTypeSchema = z.enum([
    "Owner",
    "Aunt",
    "Brother",
    "Daughter",
    "Domestic partner",
    "Father",
    "Father-in-law",
    "Grandfather",
    "Grandmother",
    "Granddaughter",
    "Grandson",
    "Mother",
    "Mother-in-law",
    "Nephew",
    "Niece",
    "Other individual",
    "Sister",
    "Son",
    "Spouse",
    "Uncle",
]);
export const HouseholdMemberSchema = z.object({
    householdId: HouseholdIdSchema,
    clientId: ClientId,
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    memberType: HouseholdMemberTypeSchema,
    pointOfContact: z.boolean().optional(),
    joinedAt: z.string().datetime().optional(),
    leftAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const HouseholdSchema = z.object({
    householdId: HouseholdIdSchema,
    // performanceGroupId: PerformanceGroupId, // remove until API is updated.
    advisorId: AdvisorId,
    advisorFirstName: z.string().optional(),
    advisorLastName: z.string().optional(),
    name: z.string(),
    establishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const HouseholdWithMembersSchema = HouseholdSchema.extend({
    members: z.array(HouseholdMemberSchema).optional(),
});
export const AccountBalanceSchema = z.object({
    physicalAccountId: z.string().uuid(),
    custodianAccountId: z.string().nullable(),
    balance: z.number().nullable(),
    clientId: ClientId,
});
export const HouseholdWithBalancesSchema = HouseholdWithMembersSchema.extend({
    totalAssets: z.string().transform((v) => new Decimal(v)),
});
export const HouseholdMemberRequestSchema = z.object({
    clientId: ClientId.describe("ID of the client to add as member"),
    memberType: HouseholdMemberTypeSchema.optional().describe("Type of household member"),
    pointOfContact: z
        .boolean()
        .optional()
        .describe("Whether this member is a point of contact for the household"),
});
// Request body schemas
export const CreateHouseholdRequestSchema = z.object({
    advisorId: AdvisorId.describe("ID of the advisor creating the household"),
    name: z.string().min(1).describe("Name of the household"),
    members: z
        .array(HouseholdMemberRequestSchema)
        .min(1)
        .describe("Array of household members (minimum 1)"),
    establishedAt: z
        .string()
        .datetime()
        .optional()
        .describe("Date when household was established"),
});
export const CreateHouseholdSuccessResponseBodySchema = z.object({
    data: HouseholdWithMembersSchema,
});
export const PostHouseholdMemberSuccessResponseBodySchema = z.object({
    data: HouseholdMemberSchema,
});
export const UpdateHouseholdRequestSchema = z.object({
    name: z.string().min(1).optional().describe("Updated name of the household"),
    members: z
        .array(HouseholdMemberRequestSchema)
        .min(1)
        .optional()
        .describe("Updated array of household members"),
    establishedAt: z
        .string()
        .datetime()
        .optional()
        .describe("Updated date when household was established"),
});
// Parameter schemas
export const HouseholdParamsSchema = z.object({
    householdId: HouseholdIdSchema,
});
export const UpdateHouseholdSuccessResponseBodySchema = z.object({
    data: HouseholdWithMembersSchema,
});
// Delete household can return different responses based on status code
export const DeleteHouseholdSuccessResponseSchema = z.string().nullish();
export const DeleteHouseholdErrorResponseSchema = z.object({
    message: z.string(),
    householdId: HouseholdIdSchema.optional(),
});
export const DeleteHouseholdResponseSchema = z.union([
    DeleteHouseholdSuccessResponseSchema,
    DeleteHouseholdErrorResponseSchema,
]);
// Query schemas
export const HouseholdQuerySchema = z.object({
    advisorId: AdvisorId.optional().describe("Filter households by advisor ID"),
});
// Response schemas
export const HouseholdResponseSchema = HouseholdWithMembersSchema;
export const HouseholdWithBalancesResponseSchema = HouseholdWithBalancesSchema;
// -- Pagination Schemas --
export const pageQueryString = z
    .string()
    .default("1")
    .transform((v) => Number(v))
    .refine((v) => Number.isInteger(v), { message: "Must be an integer" })
    .refine((v) => v >= 1, { message: "Must be greater than or equal to 1" })
    .describe("The page number, default is 1");
export const defaultPageSize = 10;
export const minPageSize = 1;
export const maxPageSize = 100;
export const pageSizeQueryString = z
    .string()
    .default(defaultPageSize.toString())
    .transform((v) => Number(v))
    .refine((v) => Number.isInteger(v), { message: "Must be an integer" })
    .refine((v) => v >= minPageSize, {
    message: `Must be greater than or equal to ${minPageSize}`,
})
    .refine((v) => v <= maxPageSize, {
    message: `Must be less than or equal to ${maxPageSize}`,
})
    .describe(`The number of items per page, default is ${defaultPageSize}. Must be between ${minPageSize} and ${maxPageSize}`);
export const HouseholdOrderBySchema = z
    .enum([
    "createdAt asc",
    "createdAt desc",
    "name asc",
    "name desc",
    "totalAssets asc",
    "totalAssets desc",
    "advisorFirstName asc",
    "advisorFirstName desc",
    "advisorLastName asc",
    "advisorLastName desc",
])
    .default("createdAt desc");
// -- Get Households Schemas --
export const GetHouseholdsQuerySchema = z.object({
    search: z.string().optional().describe("Generic search term for households"),
    page: pageQueryString.optional(),
    pageSize: pageSizeQueryString.optional(),
    orderBy: HouseholdOrderBySchema.optional(),
    advisorId: AdvisorId.optional().describe("Filter households by advisor ID"),
});
export const GetHouseholdsResponseBodySchema = z.object({
    data: z.array(HouseholdWithBalancesSchema),
    pagination: PaginationMetadata,
});
// -- Get Single Household Schemas --
export const GetSingleHouseholdParamsSchema = z.object({
    householdId: HouseholdIdSchema,
});
export const GetSingleHouseholdResponseBodySchema = z.object({
    data: HouseholdWithBalancesSchema,
});
// -- Get Household Accounts Schemas --
export const HouseholdAccountsOrderBySchema = z
    .enum(["establishedAt asc", "establishedAt desc", "name asc", "name desc"])
    .default("establishedAt desc");
export const HouseholdAccountTypePaginationSchema = z.union([
    z.literal("FartherManagedAccount"),
    z.literal("ManuallyTrackedAccount"),
    PlaidAccountTypeSchema,
]);
export const GetHouseholdAccountQuerySchema = z.object({
    search: z.string().optional().describe("Generic search term for households"),
    page: pageQueryString.optional(),
    pageSize: pageSizeQueryString.optional(),
    orderBy: HouseholdAccountsOrderBySchema.optional(),
    accountType: HouseholdAccountTypePaginationSchema.optional().describe("Filter accounts by type"),
});
const AccountsByHouseholdIdMemberSchema = z.object({
    clientId: ClientId,
    firstName: z.string().optional(),
    lastName: z.string().optional(),
});
export const HouseHoldAccountsSchema = z.discriminatedUnion("accountProvider", [
    z.object({
        accountProvider: z.literal("FartherManagedAccount"),
        accountId: FartherAccountId,
        type: AccountType,
        member: AccountsByHouseholdIdMemberSchema,
        name: z.string().nullable(), // displayName found in Shared-Library/src/api/Accounts/Types.ts
        amount: z.string().transform((v) => new Decimal(v)),
        custodian: Custodian,
        number: CustodianAccountId.nullable(),
        portfolio: PortfolioFamilyChild.nullable(),
    }),
    z.object({
        accountProvider: z.literal("ManuallyTrackedAccount"),
        accountId: AltAssetId,
        type: ManualAccountType,
        name: z.string().nullable(), // assetName found in Shared-Library/src/api/Accounts/Types.ts
        member: AccountsByHouseholdIdMemberSchema,
        amount: z.string().transform((v) => new Decimal(v)),
        custodian: Custodian.nullable(),
        number: CustodianAccountId.nullable(),
        portfolio: PortfolioFamilyChild.nullable(),
    }),
    z.object({
        accountProvider: z.literal("ExternalAccount"),
        accountId: ExternalAccountId,
        type: PlaidAccountTypeSchema,
        name: z.string().nullable(), //   name or officialName found in Shared-Library/src/api/Accounts/requests/getClientFundingAccounts.ts
        member: AccountsByHouseholdIdMemberSchema,
        amount: z.string().transform((v) => new Decimal(v)),
        custodian: Custodian.nullable(),
        number: CustodianAccountId.nullable(),
        portfolio: PortfolioFamilyChild.nullable(),
    }),
]);
export const GetHouseholdAccountsResponseBodySchema = z.object({
    data: z.array(HouseHoldAccountsSchema),
    pagination: PaginationMetadata,
});
// -- Get Household Accounts Performance Schemas --
export const PerformanceTimeRangeSchema = z.object({
    period: z.enum(["30D", "3M", "6M", "1Y", "YTD"]),
    performance: z.string(),
});
export const HouseholdPerformanceSchema = z.object({
    householdId: HouseholdIdSchema,
    asOfDate: z.string(),
    performance: z.array(PerformanceTimeRangeSchema),
});
export const GetHouseholdPerformanceResponseBodySchema = z.object({
    data: HouseholdPerformanceSchema,
});
// Backward compatibility aliases
export const UpdateHouseholdParamsSchema = HouseholdParamsSchema;
export const DeleteHouseholdParamsSchema = HouseholdParamsSchema;
export const CreateHouseholdRequestBodySchema = CreateHouseholdRequestSchema;
export const UpdateHouseholdRequestBodySchema = UpdateHouseholdRequestSchema;
export const HouseholdAccountBalanceSchema = AccountBalanceSchema;
//# sourceMappingURL=schemas.js.map